import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import GuestLayout from "./GuestLayout";
import withOutAuthentication from "../hoc/withOutAuthentication";

import {
  storeRedirectUrl,
  isValidPlatformRoute,
} from "../utils/redirectHandler";

const RouteLoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "40vh",
    }}
  >
    <CircularProgress />
  </Box>
);
const SignIn = lazy(() => import("../pages/Auth/SignIn"));
const Register = lazy(() => import("../pages/Register/Register"));
const ForgotPasswordPage = lazy(() => import("../pages/Auth/ForgotPassword"));
const VerifyEmailPage = lazy(() => import("../pages/Auth/VerifyPage"));
const PasswordResetPage = lazy(() => import("../pages/Auth/ResetPassword"));
const Homepage = lazy(() => import("../pages/LandingPage/Home"));

const ContactPage = lazy(
  () => import("../pages/LandingPage/contacts/Contactpage"),
);
const SupportPage = lazy(() => import("../components/Support/SupportPage"));
const ComingSoon = lazy(() => import("../pages/LandingPage/ComingSoon"));
const AirbankLandingPage = lazy(
  () => import("../pages/LandingPage/AirbankLandingPage"),
);
const AirbankPage = lazy(() => import("../pages/Airbank/AirbankPage"));
const AITPage = lazy(() => import("../pages/Airbank/AITPage"));
const AITCardPage = lazy(() => import("../pages/Airbank/AITCardPage"));
const AITwinTalentPage = lazy(
  () => import("../pages/Airbank/AITwinTalentPage"),
);
const AITwinUserPage = lazy(() => import("../pages/Airbank/AITwinUserPage"));

// Component to redirect unknown routes to sign-in
const RedirectToSignIn = () => {
  const location = useLocation();
  const currentUrl = window.location.href;

  // Store the redirect URL if it's a valid platform route
  if (isValidPlatformRoute(currentUrl) && !currentUrl.includes("/sign-in")) {
    storeRedirectUrl(currentUrl);
  }

  return (
    <Navigate
      to="/sign-in"
      replace
      state={{ redirect: location.pathname + location.search }}
    />
  );
};

const GuestLayoutWrapper = withOutAuthentication(GuestLayout);

const GuestRoutes = () => {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        {/* Public landing pages */}
        <Route path="/" element={<AirbankLandingPage />} />
        <Route path="/home" element={<AirbankLandingPage />} />

        {/* Airbank platform routes */}
        <Route path="/airbank" element={<AirbankPage />} />
        <Route path="/airbank/ait" element={<AITPage />} />
        <Route path="/airbank/ait-card" element={<AITCardPage />} />
        <Route path="/airbank/ai-twin" element={<AITwinTalentPage />} />
        <Route path="/airbank/ai-twin/user" element={<AITwinUserPage />} />
        <Route path="/airbank/ai-twin/user/:id" element={<AITwinUserPage />} />
        <Route path="/talent/airbank/ai-twin" element={<AITwinTalentPage />} />
        <Route
          path="/talent/airbank/ai-twin/:id"
          element={<AITwinTalentPage />}
        />

        <Route path="/contact" element={<ContactPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/coming-soon" element={<ComingSoon />} />

        {/* Auth routes */}
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/signup/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password?/OTP" element={<VerifyEmailPage />} />
        <Route
          path="/forgot-password/reset-password"
          element={<PasswordResetPage />}
        />

        {/* Guest layout routes - accessible without authentication */}
        <Route path="/" element={<GuestLayoutWrapper />}>
          {/* Booking routes - accessible without authentication, will redirect to sign-in if logged out */}
          <Route path="sessions" element={<div />} />
          <Route path="user/session/:channel_name" element={<div />} />
          <Route path="user/group-session/:channel_name" element={<div />} />
          <Route path="user/spade-session/:channel_name" element={<div />} />
          <Route path="user/gtp-session/:channel_name" element={<div />} />
          <Route
            path="user/gtp-spade-session/:channel_name"
            element={<div />}
          />
          <Route path="*" element={<RedirectToSignIn />} />
        </Route>
        <Route path="*" element={<RedirectToSignIn />} />
      </Routes>
    </Suspense>
  );
};

export default GuestRoutes;
